import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { api } from '@/lib/api'

interface Category {
  id: string
  name: string
  amount: number | null
}

interface SmsBilling {
  /** Flat rate activity categories only: an hourly one would bill a whole hour per text. */
  categories: Category[]
  received: string
  sent: string
  /** Clio refused the last line (category deleted, missing right). */
  refused: boolean
}

/**
 * Clio: the flat rate activity category of a received text and of a sent text.
 * Clio prices each line with it; Callizy never sends an amount.
 */
export function ClioTextosFactures({ companyId }: { companyId: number | undefined }) {
  const { t } = useTranslation()
  const tx = (cle: string, options?: Record<string, unknown>) => t(`manager.clio.textos.${cle}`, options)
  const queryClient = useQueryClient()
  const key = ['clio-sms-billing', companyId]
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: async () => (await api.get<SmsBilling>(`/api/companies/${companyId}/clio/sms-billing/`)).data,
    enabled: Boolean(companyId),
    staleTime: 60 * 1000,
    retry: false,
  })
  const choose = useMutation({
    mutationFn: async (body: { received?: string; sent?: string }) =>
      (await api.patch<SmsBilling>(`/api/companies/${companyId}/clio/sms-billing/`, body)).data,
    onSuccess: (next) => {
      queryClient.setQueryData(key, next)
      toast.success(tx('enregistre'))
    },
    onError: () => toast.error(tx('erreur')),
  })

  const label = (c: Category) => (c.amount == null ? c.name : tx('option', { name: c.name, amount: c.amount }))

  return (
    <div className="px-6 py-4 space-y-3">
      <div>
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{tx('titre')}</p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">{tx('texte')}</p>
      </div>
      {data?.refused ? <p className="text-sm text-red-600 dark:text-red-400">{tx('refus')}</p> : null}
      {isLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          {tx('chargement')}
        </p>
      ) : isError || !data ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{tx('illisible')}</p>
      ) : data.categories.length === 0 ? (
        <p className="text-sm text-amber-700 dark:text-amber-400">{tx('vide')}</p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {(['received', 'sent'] as const).map((sens) => (
            <div key={sens} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3">
              <label htmlFor={`clio-sms-${sens}`} className="text-sm font-medium text-slate-900 dark:text-white">
                {tx(sens === 'received' ? 'recu' : 'envoye')}
              </label>
              <select
                id={`clio-sms-${sens}`}
                value={data[sens]}
                onChange={(e) => choose.mutate({ [sens]: e.target.value })}
                disabled={choose.isPending}
                className="w-full sm:w-72 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-base sm:text-sm text-slate-900 dark:text-white disabled:opacity-60"
              >
                <option value="">{tx('aucune')}</option>
                {data.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {label(c)}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
